import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { updateMapFilters } from "@/features/auth/interactiveSurveySlice";
import { Property } from "@/features/survey/types/survey.types";
import { MediaType, useGetPropertyMediaQuery } from "@/services/mediaService";
import { CommentsSheet } from "@/shared/components/CommentsSheet";
import MediaDropdownPill from "@/shared/components/MediaDropdownPill";
import { flattenMediaFilesWithModifiedFiles } from "@/shared/utils";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import PropertyDetails from "./components/PropertyDetails";
import PropertyHeader from "./components/PropertyHeader";
import PropertyImages from "./components/PropertyImages";
import PropertyMap from "./components/PropertyMap";

const InteractiveSurveyProperty = () => {
  // Get the surveyId and propertyId from the URL parameters
  const { surveyId, propertyId } = useParams<{
    surveyId: string;
    propertyId: string;
  }>();

  const navigate = useNavigate();
  const { currentSurvey, mapFilters } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const dispatch = useAppDispatch();
  const { showShortlistOnly } = mapFilters;
  const updatedSurvey = currentSurvey
    ? {
        ...currentSurvey,
        properties: currentSurvey.properties?.filter((p) => !p.isHidden) || [],
      }
    : null;
  const [propertyList, setPropertyList] = useState<Property[]>([]);
  const [currentPropertyIndex, setCurrentPropertyIndex] = useState<number>(-1);
  const [property, setProperty] = useState<Property | null>(null);
  const [isCommentsSheetOpen, setIsCommentsSheetOpen] = useState(false);

  // Refs for scroll containers
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);

  const isPropertyHidden = currentSurvey?.properties?.find(
    (each) => each.propertyId === propertyId,
  )?.isHidden;

  const shortlisted = updatedSurvey?.properties?.filter((p) => {
    if (!p.isHidden && p.isClientShortlist) {
      if (!updatedSurvey.showCurrentLocationPD) {
        return p.isCurrentLocation ? false : true;
      } else {
        return true;
      }
    }
    return false;
  });

  // Handle hidden property redirect
  useEffect(() => {
    if (isPropertyHidden) {
      const visibleProperties = updatedSurvey?.properties?.filter(
        (each) => !each.isHidden,
      );
      const fallbackProperty = visibleProperties?.length
        ? visibleProperties[0]
        : null;
      if (updatedSurvey?.id && fallbackProperty?.id) {
        navigate(
          `/interactive/${updatedSurvey.id}/property/${fallbackProperty.id}`,
          { replace: true },
        );
      }
    }
  }, [isPropertyHidden, updatedSurvey, navigate]);

  useEffect(() => {
    if (updatedSurvey?.properties) {
      // Find the specific property from the survey data
      let property =
        !updatedSurvey?.properties || !propertyId
          ? null
          : showShortlistOnly
            ? shortlisted?.find((p: Property) => p.id === propertyId)
            : updatedSurvey.properties.find(
                (p: Property) => !p.isHidden && p.id === propertyId,
              ) || null;

      if (property) {
        const propertyName =
          property.propertyName || property.name || `Property ${property.id}`;
        document.title = propertyName;
        setProperty(property);
      } else {
        // Property not found - handle fallback logic
        const fallbackProperty = shortlisted?.length ? shortlisted[0] : null;

        if (fallbackProperty) {
          navigate(
            `/interactive/${updatedSurvey.id}/property/${fallbackProperty.id}`,
            { replace: true },
          );
        } else {
          setProperty(null);
          window.sessionStorage.setItem(
            "shortlisted",
            JSON.stringify(!showShortlistOnly),
          );
          dispatch(updateMapFilters({ showShortlistOnly: !showShortlistOnly }));
        }
      }

      // Get property list for navigation
      const allProperties = updatedSurvey?.properties || [];

      // Use the survey's showCurrentLocationPD setting to determine filtering
      // If showCurrentLocationPD is true: show all properties
      // If showCurrentLocationPD is false: don't show current location properties
      const list = updatedSurvey?.showCurrentLocationPD
        ? allProperties // Show all properties when showCurrentLocationPD is true
        : allProperties.filter((prop: Property) => !prop.isCurrentLocation); // Filter out current location properties when false

      const filteredList = showShortlistOnly
        ? list.filter((p: Property) => !p.isHidden && p.isClientShortlist)
        : list;
      setPropertyList(filteredList);

      // Traditional index for navigation purposes (using filtered list)
      setCurrentPropertyIndex(
        property
          ? filteredList.findIndex((p: Property) => p.id === property.id)
          : -1,
      );
    }

    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = "Interactive Survey";
    };
  }, [shortlisted?.length, showShortlistOnly, propertyId]);

  // Reset scroll position when property changes
  useEffect(() => {
    if (leftScrollRef.current) {
      leftScrollRef.current.scrollTop = 0;
    }
    if (rightScrollRef.current) {
      rightScrollRef.current.scrollTop = 0;
    }
  }, [propertyId]);

  // Fetch property flyers - only if isIncludeFlyers is true
  const { data: propertyFlyers } = useGetPropertyMediaQuery(
    {
      surveyId: surveyId!,
      propertyId: propertyId!,
      mediaType: MediaType.FLIERS,
    },
    {
      skip:
        !surveyId ||
        !propertyId ||
        !currentSurvey ||
        !currentSurvey.isIncludeFlyers,
    },
  );

  // Fetch property floor plans
  const { data: propertyFloorPlans } = useGetPropertyMediaQuery(
    {
      surveyId: surveyId!,
      propertyId: propertyId!,
      mediaType: MediaType.FLOORPLANS,
    },
    { skip: !surveyId || !propertyId || !currentSurvey },
  );

  // Process flyers: filter hidden files and flatten modified versions
  const processedFlyers = useMemo(() => {
    if (!propertyFlyers) return [];
    const visibleFlyers = propertyFlyers.filter((media) => !media.isHidden);
    return flattenMediaFilesWithModifiedFiles(visibleFlyers);
  }, [propertyFlyers]);

  // Handle case where property is not found or hidden
  if (
    isPropertyHidden &&
    !updatedSurvey?.properties?.filter((each) => !each.isHidden).length
  ) {
    return (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Properties Found
          </h3>
        </div>
      </div>
    );
  }

  // Handle case where property is not found
  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 overflow-auto">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-2">
            Property Not Found
          </h2>
          <p className="text-gray-500">Unable to load property details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {property && updatedSurvey && (
        <div className="h-24 z-40 shrink-0">
          <PropertyHeader
            property={property}
            propertyId={propertyId || ""}
            currentSurvey={updatedSurvey}
            currentPropertyIndex={currentPropertyIndex}
            propertyList={propertyList}
          />
        </div>
      )}

      <div className="flex-1 flex flex-col lg:flex-row lg:overflow-hidden">
        <div className="property-image" ref={leftScrollRef}>
          {/* Flyers and Floor Plans */}
          {(currentSurvey?.isIncludeFlyers ||
            currentSurvey?.isIncludeSitePlans) && (
            <div className="flex lg:hidden bg-white p-4">
              <div className="flex flex-wrap gap-3">
                {/* Floor Plans */}
                {currentSurvey?.isIncludeSitePlans &&
                  (propertyFloorPlans?.length ?? 0) > 0 && (
                    <MediaDropdownPill
                      icon="layers"
                      label={currentSurvey?.sitePlanLabel || "Site Plans"}
                      files={propertyFloorPlans!}
                    />
                  )}

                {/* Flyers */}
                {currentSurvey?.isIncludeFlyers &&
                  processedFlyers.length > 0 && (
                    <MediaDropdownPill
                      icon="menu_book"
                      label={currentSurvey?.flyerLabel || "Flyers"}
                      files={processedFlyers}
                    />
                  )}
              </div>
            </div>
          )}
          <PropertyImages />
        </div>

        <div className="prop-stuff-container" ref={rightScrollRef}>
          {/* Flyers and Floor Plans */}
          {(currentSurvey?.isIncludeFlyers ||
            currentSurvey?.isIncludeSitePlans) && (
            <div className="hidden lg:flex bg-white lg:py-3">
              <div className="flex flex-wrap gap-3">
                {/* Floor Plans */}
                {currentSurvey?.isIncludeSitePlans &&
                  (propertyFloorPlans?.length ?? 0) > 0 && (
                    <MediaDropdownPill
                      icon="layers"
                      label={currentSurvey?.sitePlanLabel || "Site Plans"}
                      files={propertyFloorPlans!}
                    />
                  )}

                {/* Flyers */}
                {currentSurvey?.isIncludeFlyers &&
                  processedFlyers.length > 0 && (
                    <MediaDropdownPill
                      icon="menu_book"
                      label={currentSurvey?.flyerLabel || "Flyers"}
                      files={processedFlyers}
                    />
                  )}
              </div>
            </div>
          )}
          <div className="property-map">
            {property && <PropertyMap property={property} />}
          </div>

          {/* Bottom Section - Property Details */}
          {property && updatedSurvey && (
            <PropertyDetails
              property={property}
              currentSurvey={updatedSurvey}
            />
          )}
        </div>
      </div>

      {/* Comments Sheet */}
      {property && updatedSurvey && (
        <CommentsSheet
          isOpen={isCommentsSheetOpen}
          onClose={() => setIsCommentsSheetOpen(false)}
          property={property}
          survey={updatedSurvey}
        />
      )}
    </div>
  );
};

export default InteractiveSurveyProperty;
